import { useState, useEffect } from 'react';
import { blockscoutFetch, formatDeploymentDate } from '../lib/blockscout';
import { getExplorerApiConfig } from '@/lib/explorerApi';

interface UseContractDeploymentDateResult {
  deploymentDate: string | null;
  isLoading: boolean;
  error: string | null;
}

async function fetchBlockscoutDeploymentDate(
  explorerBaseUrl: string,
  bridgeAddress: string,
): Promise<string | null> {
  const addressRes = await blockscoutFetch(
    explorerBaseUrl,
    `/api/v2/addresses/${bridgeAddress}`,
  );

  if (!addressRes.ok) {
    throw new Error(`Blockscout API error: ${addressRes.status}`);
  }

  const addressData = await addressRes.json();
  const creationTxHash: string | undefined = addressData.creation_transaction_hash;
  if (!creationTxHash) return null;

  const txRes = await blockscoutFetch(
    explorerBaseUrl,
    `/api/v2/transactions/${creationTxHash}`,
  );

  if (!txRes.ok) {
    throw new Error(`Blockscout API error: ${txRes.status}`);
  }

  const txData = await txRes.json();
  return formatDeploymentDate(txData.timestamp);
}

async function fetchEtherscanDeploymentDate(
  apiBaseUrl: string,
  bridgeAddress: string,
): Promise<string | null> {
  const creationUrl = new URL(apiBaseUrl);
  creationUrl.searchParams.set('module', 'contract');
  creationUrl.searchParams.set('action', 'getcontractcreation');
  creationUrl.searchParams.set('contractaddresses', bridgeAddress);

  const creationRes = await fetch(creationUrl.toString());
  if (!creationRes.ok) {
    throw new Error(`Explorer API error: ${creationRes.status}`);
  }

  const creationData = await creationRes.json();
  const txHash: string | undefined = Array.isArray(creationData.result)
    ? creationData.result[0]?.txHash
    : undefined;
  if (!txHash) return null;

  const txUrl = new URL(apiBaseUrl);
  txUrl.searchParams.set('module', 'proxy');
  txUrl.searchParams.set('action', 'eth_getTransactionByHash');
  txUrl.searchParams.set('txhash', txHash);

  const txRes = await fetch(txUrl.toString());
  if (!txRes.ok) {
    throw new Error(`Explorer API error: ${txRes.status}`);
  }

  const txData = await txRes.json();
  const blockHex: string | undefined = txData.result?.blockNumber;
  if (!blockHex) return null;

  const blockUrl = new URL(apiBaseUrl);
  blockUrl.searchParams.set('module', 'proxy');
  blockUrl.searchParams.set('action', 'eth_getBlockByNumber');
  blockUrl.searchParams.set('tag', blockHex);
  blockUrl.searchParams.set('boolean', 'false');

  const blockRes = await fetch(blockUrl.toString());
  if (!blockRes.ok) {
    throw new Error(`Explorer API error: ${blockRes.status}`);
  }

  const blockData = await blockRes.json();
  const tsHex: string | undefined = blockData.result?.timestamp;
  if (!tsHex) return null;

  return formatDeploymentDate(new Date(parseInt(tsHex, 16) * 1000).toISOString());
}

export function useContractDeploymentDate(
  chainId: number | null,
  bridgeAddress: string,
): UseContractDeploymentDateResult {
  const [deploymentDate, setDeploymentDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (chainId == null || !bridgeAddress) {
      setDeploymentDate(null);
      setError(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // Clear previous chain's date immediately on switch.
    setDeploymentDate(null);
    setError(null);
    setIsLoading(true);

    async function fetchDeploymentDate() {
      try {
        const config = getExplorerApiConfig(chainId!);
        const formatted =
          config.kind === 'etherscan'
            ? await fetchEtherscanDeploymentDate(config.apiBaseUrl, bridgeAddress)
            : await fetchBlockscoutDeploymentDate(config.apiBaseUrl, bridgeAddress);

        if (!cancelled) {
          setDeploymentDate(formatted);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch deployment date');
          setIsLoading(false);
        }
      }
    }

    void fetchDeploymentDate();

    return () => {
      cancelled = true;
    };
  }, [chainId, bridgeAddress]);

  return { deploymentDate, isLoading, error };
}
